<template>
  <div
    :data-testid="dataTestid"
    class="flex flex-col gap-2"
  >
    <strong :class="titleClass">Who's travelling?</strong>

    <p :class="['text-sm text-semantic-surface-on-surface-variant whitespace-pre-line', descriptionClass]">
      {{ description }}
    </p>

    <div
      v-for="(traveller, index) in travellers"
      :key="traveller.uid"
      class="flex flex-col gap-1"
    >
      <div class="flex items-center gap-2">
        <span class="w-20 shrink-0">Traveller {{ index + 1 }}</span>

        <V2AtomsInput
          :ref="el => (inputRefs[index] = el)"
          v-model="traveller.age"
          placeholder="Age"
          type="text"
          model-type="text"
          mobile-keyboard-type="numeric"
          maxlength="3"
          hide-icon
          wrapper-class="w-28 shrink-0"
          :aria-label="`Traveller ${index + 1} age`"
          :input-area-class="rowErrors[index] ? '!border-semantic-states-error-default' : ''"
          :data-testid="`traveller-age-${index}`"
          @beforeinput="blockNonDigitInput"
        />

        <V2AtomsLabel
          v-if="classifyTravellerAge(traveller.age)"
          size="small"
          theme="default"
          :data-testid="`traveller-badge-${index}`"
        >
          {{ classifyTravellerAge(traveller.age) === 'adult' ? 'Adult' : 'Dependant' }}
        </V2AtomsLabel>

        <button
          v-if="travellers.length > 1"
          type="button"
          class="ml-auto"
          :aria-label="`Remove traveller ${index + 1}`"
          :data-testid="`traveller-remove-${index}`"
          @click="removeTraveller(index)"
        >
          <V2IconsCancelCircle :fill="semanticColorLookup('surface.on-surface-variant')" />
        </button>
      </div>

      <p
        v-if="rowErrors[index]"
        class="text-xs text-semantic-states-error-default"
        :data-testid="`traveller-error-${index}`"
      >
        {{ rowErrors[index] }}
      </p>
    </div>

    <V2AtomsButton
      theme="tertiary"
      size="small"
      class="self-start"
      data-testid="traveller-add"
      @click="addTraveller"
    >
      + Add traveller
    </V2AtomsButton>

    <p
      v-if="groupError"
      class="text-xs text-semantic-states-error-default"
      data-testid="traveller-ages-error"
    >
      {{ groupError }}
    </p>

    <p
      v-if="summary"
      class="text-semantic-primary-variant-dark"
      data-testid="traveller-summary"
    >
      {{ summary }}
    </p>
  </div>
</template>

<script setup>
import { TRAVELLER_AGE_ERRORS } from '~/constants/funnel/travel-insurance'

defineOptions({
  name: 'V2MoleculesTravelInsuranceTravellerAges'
})

defineProps({
  dataTestid: {
    type: String,
    default: ''
  },

  titleClass: {
    type: String,
    default: ''
  },

  descriptionClass: {
    type: String,
    default: ''
  },

  description: {
    type: String,
    default: 'Add the age of everyone travelling on this policy.'
  }
})

const adultAges = defineModel('adultAges', {
  type: String,
  default: null
})
const dependantAges = defineModel('dependantAges', {
  type: String,
  default: null
})

let uid = 0

function createRow (age = '') {
  return { uid: uid++, age }
}

const travellers = ref([createRow()])
const rowErrors = ref([''])
const groupError = ref('')
const inputRefs = ref([])
let lastEmitted

const summary = computed(() => summariseTravellers(travellers.value))

function modelKey (adult, dependant) {
  return JSON.stringify([adult ?? null, dependant ?? null])
}

watch([adultAges, dependantAges], ([adult, dependant]) => {
  if (modelKey(adult, dependant) === lastEmitted) {
    return
  }

  travellers.value = parseAgesToTravellers(adult, dependant)
    .map(({ age }) => createRow(age))
  rowErrors.value = travellers.value.map(() => '')
  groupError.value = ''
}, { immediate: true })

watch(travellers, () => {
  travellers.value.forEach(traveller => {
    const sanitised = String(traveller.age ?? '')
      .replace(/\D/g, '')
      .replace(/^0+(?=\d)/, '')

    if (traveller.age !== sanitised) {
      traveller.age = sanitised
    }
  })

  const derived = travellersToAgeStrings(travellers.value)

  lastEmitted = modelKey(derived.adultAges, derived.dependantAges)

  if (adultAges.value !== derived.adultAges) {
    adultAges.value = derived.adultAges
  }

  if (dependantAges.value !== derived.dependantAges) {
    dependantAges.value = derived.dependantAges
  }

  rowErrors.value = rowErrors.value.map((error, index) => (
    error ? validateRow(index) : ''
  ))

  if (groupError.value) {
    groupError.value = validateGroup()
  }
}, { deep: true })

onBeforeUpdate(() => {
  inputRefs.value = []
})

function blockNonDigitInput ($event) {
  const text = $event.data ?? $event.dataTransfer?.getData('text') ?? ''

  if (/\D/.test(text)) {
    $event.preventDefault()
  }
}

function addTraveller () {
  travellers.value.push(createRow())
  rowErrors.value.push('')
}

function removeTraveller (index) {
  travellers.value.splice(index, 1)
  rowErrors.value.splice(index, 1)
}

function validateRow (index) {
  const age = String(travellers.value[index]?.age ?? '').trim()

  if (!age) {
    return TRAVELLER_AGE_ERRORS.required
  }

  return classifyTravellerAge(age) ? '' : TRAVELLER_AGE_ERRORS.range
}

function validateGroup () {
  const hasAdult = travellers.value
    .some(({ age }) => classifyTravellerAge(age) === 'adult')

  return hasAdult ? '' : TRAVELLER_AGE_ERRORS.adultRequired
}

function validate ({ focus = true } = {}) {
  rowErrors.value = travellers.value.map((_, index) => validateRow(index))
  groupError.value = validateGroup()

  const firstInvalid = rowErrors.value.findIndex(Boolean)
  const isValid = firstInvalid === -1 && !groupError.value

  if (!isValid && focus) {
    const target = inputRefs.value[firstInvalid === -1 ? 0 : firstInvalid]

    target?.input?.focus()
  }

  return isValid
}

defineExpose({
  validate,
  travellers
})
</script>
